import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  CardMedia,
  CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { useTranslation } from './translations';

function QuantumCats({
  searchQuery,
  sortBy,
  sortDirection,
  selectedAttributes,
  setSelectedAttributes,
  language,
  quantumCatsData,
  onNftsLoaded
}) {
  const [nfts, setNfts] = useState([]);
  const [filteredNfts, setFilteredNfts] = useState([]);
  const [selectedNft, setSelectedNft] = useState(null);
  const [attributeFrequency, setAttributeFrequency] = useState({});
  const [failedImages, setFailedImages] = useState(new Set());
  const t = useTranslation()[language];

  // Функция для формирования URL изображения
  const getImageUrl = (displayName) => {
    // Формируем URL для изображения
    const catNumber = displayName.replace('cat', '');
    
    // Используем только локальные WebP изображения
    return `/images/quantum_cats_webp/cat${catNumber}.webp`;
  };

  // Мемоизируем обработку данных
  const processedData = useMemo(() => {
    if (!quantumCatsData) return null;

    // Подсчитываем частоту каждого значения атрибута
    const frequency = {};
    const availableAttrs = {};
    quantumCatsData.forEach(nft => {
      if (nft.attributes && nft.attributes.length > 0) {
        nft.attributes.forEach(attr => {
          const key = `${attr.trait_type}:${attr.value}`;
          frequency[key] = (frequency[key] || 0) + 1;
          
          if (!availableAttrs[attr.trait_type]) {
            availableAttrs[attr.trait_type] = new Set();
          }
          availableAttrs[attr.trait_type].add(attr.value);
        });
      }
    });

    // Вычисляем редкость только для NFT с атрибутами
    const processedNfts = quantumCatsData.map(nft => {
      if (nft.attributes && nft.attributes.length > 0) {
        const rarityScores = nft.attributes
          .filter(attr => attr.trait_type !== "Quantum State")
          .map(attr => {
            const key = `${attr.trait_type}:${attr.value}`;
            const attrFrequency = frequency[key];
            const totalNfts = quantumCatsData.filter(n => n.attributes && n.attributes.length > 0).length;
            return 1 - (attrFrequency / totalNfts);
          });

        const rarityScore = rarityScores.length > 0 
          ? rarityScores.reduce((a, b) => a + b, 0) / rarityScores.length 
          : 0;

        const quantumState = nft.attributes.find(attr => attr.trait_type === "Quantum State");
        return {
          ...nft,
          rarityScore,
          quantumState: quantumState ? quantumState.value : null
        };
      }
      return {
        ...nft,
        rarityScore: 0,
        quantumState: null
      };
    });

    // Разделяем на Dead и Alive
    const deadNfts = processedNfts.filter(nft => nft.quantumState === "Dead");
    const aliveNfts = processedNfts.filter(nft => nft.quantumState === "Alive");
    const noStateNfts = processedNfts.filter(nft => !nft.quantumState);

    // Сортируем каждую группу по редкости
    const sortedDeadNfts = [...deadNfts].sort((a, b) => b.rarityScore - a.rarityScore);
    const sortedAliveNfts = [...aliveNfts].sort((a, b) => b.rarityScore - a.rarityScore);
    
    // Создаем чередующийся список с рангами
    const allNfts = [];
    const maxLength = Math.max(sortedDeadNfts.length, sortedAliveNfts.length);
    
    for (let i = 0; i < maxLength; i++) {
      if (i < sortedDeadNfts.length) {
        allNfts.push({
          ...sortedDeadNfts[i],
          rank: i + 1,
          state: 'Dead'
        });
      }
      if (i < sortedAliveNfts.length) {
        allNfts.push({
          ...sortedAliveNfts[i],
          rank: i + 1,
          state: 'Alive'
        });
      }
    }
    
    // Добавляем NFT без состояния в конец
    allNfts.push(...noStateNfts.map(nft => ({ ...nft, rank: 1 })));

    return {
      frequency,
      availableAttrs,
      processedNfts: allNfts
    };
  }, [quantumCatsData]);

  // Мемоизируем фильтрацию и сортировку
  const filteredAndSortedNfts = useMemo(() => {
    if (!processedData) return [];

    let filtered = [...processedData.processedNfts];

    // Фильтрация по поиску
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(nft => 
        nft.displayName.toLowerCase().includes(query)
      );
    }

    // Фильтрация по атрибутам
    if (Object.keys(selectedAttributes).length > 0) {
      filtered = filtered.filter(nft => {
        return Object.entries(selectedAttributes).every(([trait, value]) => {
          if (!value || value === "Все") return true;
          return nft.attributes && nft.attributes.some(attr => 
            attr.trait_type === trait && attr.value === value
          );
        });
      });
    }

    // Сортировка
    if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const numA = parseInt(a.displayName.replace('cat', ''));
        const numB = parseInt(b.displayName.replace('cat', ''));
        return sortDirection === 'asc' ? numA - numB : numB - numA;
      });
    } else {
      const deadNfts = filtered.filter(nft => nft.quantumState === "Dead");
      const aliveNfts = filtered.filter(nft => nft.quantumState === "Alive");
      const noStateNfts = filtered.filter(nft => !nft.quantumState);

      const sortedDeadNfts = [...deadNfts].sort((a, b) => 
        sortDirection === 'asc' ? a.rarityScore - b.rarityScore : b.rarityScore - a.rarityScore
      );
      const sortedAliveNfts = [...aliveNfts].sort((a, b) => 
        sortDirection === 'asc' ? a.rarityScore - b.rarityScore : b.rarityScore - a.rarityScore
      );

      filtered = [];
      const maxLength = Math.max(sortedDeadNfts.length, sortedAliveNfts.length);
      
      for (let i = 0; i < maxLength; i++) {
        if (i < sortedDeadNfts.length) {
          filtered.push({
            ...sortedDeadNfts[i],
            rank: sortedDeadNfts[i].rank,
            state: 'Dead'
          });
        }
        if (i < sortedAliveNfts.length) {
          filtered.push({
            ...sortedAliveNfts[i],
            rank: sortedAliveNfts[i].rank,
            state: 'Alive'
          });
        }
      }

      filtered.push(...noStateNfts.map(nft => ({ ...nft, rank: nft.rank })));
    }

    return filtered;
  }, [processedData, searchQuery, sortBy, sortDirection, selectedAttributes]);

  // Обновляем состояние при изменении данных
  useEffect(() => {
    if (processedData) {
      setNfts(processedData.processedNfts);
      setFilteredNfts(filteredAndSortedNfts);
      setAttributeFrequency(processedData.frequency);
      onNftsLoaded(processedData.processedNfts);
    }
  }, [processedData, filteredAndSortedNfts, onNftsLoaded]);

  const handleImageError = (displayName) => {
    console.error(`Failed to load image for cat ${displayName}`);
    setFailedImages(prev => new Set([...prev, displayName]));
  };

  const handleOpen = (nft) => {
    setSelectedNft(nft);
  };

  const handleClose = () => {
    setSelectedNft(null);
  };

  // Бесконечная прокрутка
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const lastNftRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleRange.end < filteredNfts.length) {
          setVisibleRange(prev => ({
            start: prev.start,
            end: Math.min(prev.end + 50, filteredNfts.length)
          }));
        }
      },
      { threshold: 0.1 }
    );

    if (lastNftRef.current) {
      observer.observe(lastNftRef.current);
    }

    return () => observer.disconnect();
  }, [filteredNfts.length, visibleRange]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Основной контент */}
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        {filteredNfts.slice(visibleRange.start, visibleRange.end).map((nft, index) => {
          const isDead = nft.quantumState === "Dead";
          const isAlive = nft.quantumState === "Alive";
          
          return (
            <Grid item xs={6} sm={4} md={3} lg={2} key={nft.id} ref={index === visibleRange.end - 1 ? lastNftRef : null}>
              <Card 
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpen(nft);
                }}
                sx={{ 
                  cursor: 'pointer',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isDead ? 'rgba(255, 0, 0, 0.3)' : isAlive ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                    borderColor: isDead ? 'rgba(255, 0, 0, 0.5)' : isAlive ? 'rgba(0, 255, 0, 0.5)' : 'rgba(255, 255, 255, 0.2)'
                  }
                }}
              >
                <Box sx={{ position: 'relative', paddingTop: '100%' }}>
                  {failedImages.has(nft.displayName) ? (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '16px 16px 0 0'
                      }}
                    >
                      <ImageNotSupportedIcon sx={{ fontSize: 40, color: 'rgba(255, 255, 255, 0.3)' }} />
                    </Box>
                  ) : (
                    <CardMedia
                      component="img"
                      image={getImageUrl(nft.displayName)}
                      alt={nft.displayName}
                      onError={() => handleImageError(nft.displayName)}
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '16px 16px 0 0'
                      }}
                    />
                  )}
                </Box>
                <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="subtitle2"
                    sx={{ 
                      color: 'white',
                      fontWeight: 500,
                      mb: 0.5
                    }}
                  >
                    {nft.displayName}
                  </Typography>
                  <Box sx={{ mt: 'auto' }}>
                    <Typography 
                      variant="caption"
                      sx={{ 
                        color: isDead ? 'rgba(255, 0, 0, 0.7)' : isAlive ? 'rgba(0, 255, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      {t.rank}: #{nft.rank} {nft.quantumState ? `(${nft.quantumState})` : ''}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Диалоговое окно с деталями */}
      <Dialog
        open={Boolean(selectedNft)}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'rgba(18, 18, 18, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: '90vh', sm: '95vh' },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography 
                variant="h6"
                sx={{ 
                  fontWeight: 600,
                  color: 'white'
                }}
              >
                {selectedNft?.displayName}
              </Typography>
              <Typography 
                variant="subtitle1"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  mt: 0.5
                }}
              >
                #{selectedNft?.rank} {t.inCollection}
                {selectedNft?.quantumState === "Dead" && ` (${t.rankAmongDead})`}
                {selectedNft?.quantumState === "Alive" && ` (${t.rankAmongAlive})`}
              </Typography>
              <Typography 
                variant="subtitle2"
                sx={{ 
                  color: selectedNft?.quantumState === "Dead" 
                    ? 'rgba(255, 0, 0, 0.7)' 
                    : selectedNft?.quantumState === "Alive" 
                      ? 'rgba(0, 255, 0, 0.7)' 
                      : 'rgba(255, 255, 255, 0.7)',
                  mt: 0.5
                }}
              >
                {t.rank}: #{selectedNft?.rank} {selectedNft?.quantumState ? `(${selectedNft.quantumState})` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                href={`https://magiceden.io/ordinals/item-details/${selectedNft?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                MagicEden
              </Button>
              <IconButton 
                onClick={handleClose}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNft && (
            <Grid container spacing={{ xs: 2, sm: 4 }}>
              {/* Левая колонка с изображением */}
              <Grid item xs={12} md={6}>
                {failedImages.has(selectedNft.displayName) ? (
                  <Box
                    height={{ xs: 300, sm: 400, md: 600 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ 
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <ImageNotSupportedIcon sx={{ fontSize: { xs: 40, sm: 60, md: 80 }, color: 'rgba(255, 255, 255, 0.3)' }} />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height={{ xs: 300, sm: 400, md: 600 }}
                    image={getImageUrl(selectedNft.displayName)}
                    alt={selectedNft.displayName}
                    onError={() => handleImageError(selectedNft.displayName)}
                    sx={{ 
                      borderRadius: 2,
                      objectFit: 'contain'
                    }}
                  />
                )}
              </Grid>

              {/* Правая колонка с атрибутами */}
              <Grid item xs={12} md={6}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'white',
                    mb: 3
                  }}
                >
                  {t.attributes}
                </Typography>

                {selectedNft.attributes && selectedNft.attributes.length > 0 ? (
                  <Grid container spacing={2}>
                    {selectedNft.attributes.map((attr, index) => {
                      const frequency = attributeFrequency[`${attr.trait_type}:${attr.value}`] || 0;
                
                      const percentage = (frequency / nfts.length * 100).toFixed(2);

                      return (
                        <Grid item xs={12} key={index}>
                          <Box
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.03)',
                              borderRadius: 2,
                              p: 2,
                              border: '1px solid',
                              borderColor: 'rgba(255, 255, 255, 0.05)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                transform: 'translateY(-2px)',
                                borderColor: 'rgba(255, 255, 255, 0.1)'
                              }
                            }}
                          >
                            <Typography 
                              variant="subtitle2"
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.7)',
                                mb: 0.5
                              }}
                            >
                              {attr.trait_type}
                            </Typography>
                            <Typography 
                              variant="body1"
                              sx={{ 
                                color: 'white',
                                fontWeight: 500,
                                mb: 1
                              }}
                            >
                              {attr.value}
                            </Typography>
                            <Box 
                              sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <Typography 
                                variant="caption"
                                sx={{ 
                                  color: 'rgba(255, 255, 255, 0.5)'
                                }}
                              >
                                {frequency} {t.items} ({percentage}%)
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 107, 107, 0.05)',
                      borderRadius: 2,
                      p: 3,
                      border: '1px solid',
                      borderColor: 'rgba(255, 107, 107, 0.1)',
                      textAlign: 'center'
                    }}
                  >
                    <Typography 
                      variant="body1"
                      sx={{ 
                        color: 'rgba(255, 107, 107, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      {t.noAttributes}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default QuantumCats; 